import { useEffect, useMemo, useState } from "react";
import {
  INVOICE_TEMPLATE_GLOBAL,
  useDeleteTenantInvoiceTemplate,
  useInvoiceTemplate,
  useInvoiceTemplateOverrides,
  useInvoiceTemplatePreview,
  useRemoveInvoiceTemplateLogo,
  useSaveInvoiceTemplate,
  useTenants,
  useUploadInvoiceTemplateLogo,
  type InvoiceTemplateFields,
  type InvoiceTemplateView,
} from "../api/hooks";
import { Button, Card, ErrorNotice, Field, Input, Notice, PageHeader, Select, Spinner, Textarea } from "../components/ui";

/**
 * Plantilla de factura (solo super-admin).
 *
 * Aquí viven los datos del EMISOR (quien factura) y el aspecto de la factura fiscal en PDF. Hay una plantilla
 * global y, opcionalmente, una por empresa: cada campo que una empresa deja vacío hereda el valor de la global.
 * El PDF se genera una sola vez al numerar la factura, así que cambiar la plantilla no altera las ya emitidas.
 */

type FormState = Record<keyof InvoiceTemplateFields, string>;

const EMPTY_FORM: FormState = {
  primaryColorHex: "",
  issuerName: "",
  issuerNif: "",
  issuerAddress: "",
  issuerPostalCode: "",
  issuerCity: "",
  issuerProvince: "",
  issuerCountry: "",
  issuerEmail: "",
  issuerPhone: "",
  issuerWeb: "",
  issuerIban: "",
  footerText: "",
  notesText: "",
};

function toForm(fields: InvoiceTemplateFields): FormState {
  const form = { ...EMPTY_FORM };
  for (const key of Object.keys(form) as Array<keyof FormState>) form[key] = fields[key] ?? "";
  return form;
}

function toPayload(form: FormState): Partial<InvoiceTemplateFields> {
  const payload: Partial<InvoiceTemplateFields> = {};
  for (const key of Object.keys(form) as Array<keyof FormState>) payload[key] = form[key].trim() || null;
  return payload;
}

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  let binary = "";
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary);
}

const LOGO_SOURCE_LABEL: Record<InvoiceTemplateView["effectiveLogoSource"], string> = {
  tenant: "el logo propio de esta empresa",
  global: "el logo de la plantilla global",
  platform: "el logo de la marca de la aplicación",
  none: "ningún logo (la factura sale solo con texto)",
};

export function InvoiceTemplatePage() {
  const tenants = useTenants();
  const overrides = useInvoiceTemplateOverrides();
  const [scope, setScope] = useState<string>(INVOICE_TEMPLATE_GLOBAL);
  const view = useInvoiceTemplate(scope);
  const save = useSaveInvoiceTemplate();
  const remove = useDeleteTenantInvoiceTemplate();
  const upload = useUploadInvoiceTemplateLogo();
  const removeLogo = useRemoveInvoiceTemplateLogo();
  const preview = useInvoiceTemplatePreview();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const isGlobal = scope === INVOICE_TEMPLATE_GLOBAL;
  const withOwn = useMemo(() => new Set(overrides.data?.tenantIds ?? []), [overrides.data]);

  // Al cambiar de ámbito o llegar datos nuevos del servidor, el formulario se rellena con lo guardado.
  useEffect(() => {
    if (view.data && view.data.scope === scope) {
      setForm(toForm(view.data.own));
      setDirty(false);
    }
  }, [view.data, scope]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const change = (key: keyof FormState, value: string): void => {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
  };

  const changeScope = (next: string): void => {
    setScope(next);
    setMessage(null);
    setPreviewUrl(null);
  };

  const data = view.data && view.data.scope === scope ? view.data : null;
  const inherited = data?.inherited ?? null;

  const input = (key: keyof FormState, label: string, extra: { hint?: string; placeholder?: string } = {}) => {
    const inheritedValue = !isGlobal ? (inherited?.[key] ?? "") : "";
    return (
      <Field label={label} htmlFor={`tpl-${key}`} hint={extra.hint ?? (!isGlobal && inheritedValue ? `Vacío: hereda «${inheritedValue}»` : undefined)}>
        <Input
          id={`tpl-${key}`}
          value={form[key]}
          placeholder={extra.placeholder ?? (inheritedValue || undefined)}
          onChange={(event) => change(key, event.target.value)}
        />
      </Field>
    );
  };

  async function handleSave(): Promise<void> {
    setMessage(null);
    try {
      await save.mutateAsync({ scope, fields: toPayload(form) });
      setDirty(false);
      setMessage({ tone: "ok", text: "Plantilla guardada. Se aplicará a las facturas que se numeren a partir de ahora." });
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo guardar" });
    }
  }

  async function handleRemoveTenantTemplate(): Promise<void> {
    if (!window.confirm("¿Quitar la plantilla propia de esta empresa? Volverá a usar la plantilla global.")) return;
    setMessage(null);
    try {
      await remove.mutateAsync(scope);
      setMessage({ tone: "ok", text: "Plantilla propia eliminada: esta empresa usa ahora la global." });
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo quitar" });
    }
  }

  async function handlePreview(): Promise<void> {
    setMessage(null);
    try {
      const blob = await preview.mutateAsync({ scope, draft: toPayload(form) });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo generar la vista previa" });
    }
  }

  async function handleLogo(file: File): Promise<void> {
    setMessage(null);
    try {
      const base64 = await fileToBase64(file);
      await upload.mutateAsync({ scope, filename: file.name, contentType: file.type, base64 });
      setMessage({ tone: "ok", text: "Logo actualizado." });
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo subir el logo" });
    }
  }

  async function handleRemoveLogo(): Promise<void> {
    setMessage(null);
    try {
      await removeLogo.mutateAsync(scope);
      setMessage({ tone: "ok", text: "Logo propio quitado." });
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo quitar el logo" });
    }
  }

  const tenantItems = tenants.data?.items ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Facturación"
        title="Plantilla de factura"
        description="Datos del emisor (quien factura), color, logo y textos de la factura fiscal en PDF. Hay una plantilla global y, si hace falta, una por empresa: lo que una empresa deja vacío lo hereda de la global."
      />

      <div className="space-y-4">
        <Card>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Plantilla de" htmlFor="tpl-scope">
              <Select id="tpl-scope" value={scope} onChange={(event) => changeScope(event.target.value)}>
                <option value={INVOICE_TEMPLATE_GLOBAL}>Global (para todas las empresas)</option>
                {tenantItems.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                    {withOwn.has(tenant.id) ? " — tiene plantilla propia" : ""}
                  </option>
                ))}
              </Select>
            </Field>
            <p className="self-end text-sm text-ink-600 dark:text-ink-300">
              {isGlobal
                ? "La global se usa para toda empresa que no tenga plantilla propia, y de ella heredan los campos vacíos."
                : data?.exists
                  ? "Esta empresa tiene plantilla propia: manda lo que rellenes aquí; el resto se hereda de la global."
                  : "Esta empresa usa la global. Si rellenas algún campo y guardas, se crea su plantilla propia."}
            </p>
          </div>
        </Card>

        {view.isPending ? <Spinner label="Cargando plantilla" /> : null}
        {view.isError ? <ErrorNotice error={view.error} onRetry={() => void view.refetch()} /> : null}

        {data && data.missingIssuerFields.length > 0 ? (
          <Notice tone="warn">
            <strong>Faltan datos del emisor para generar facturas:</strong>{" "}
            {data.missingIssuerFields.map((field) => field.label).join(", ")}.
            {" "}Hasta completarlos, la factura fiscal no se puede generar (la factura se numera igualmente).
          </Notice>
        ) : null}

        {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}

        {data ? (
          <div className="grid gap-4 lg:grid-cols-[1fr_26rem]">
            <div className="space-y-4">
              <Card>
                <h2 className="micro-label mb-3">Datos del emisor</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {input("issuerName", "Razón social *")}
                  {input("issuerNif", "NIF *")}
                  <div className="sm:col-span-2">{input("issuerAddress", "Dirección *")}</div>
                  {input("issuerPostalCode", "Código postal *")}
                  {input("issuerCity", "Población *")}
                  {input("issuerProvince", "Provincia")}
                  {input("issuerCountry", "País", { placeholder: "España" })}
                  {input("issuerEmail", "Correo")}
                  {input("issuerPhone", "Teléfono")}
                  {input("issuerWeb", "Web")}
                  {input("issuerIban", "IBAN", { hint: "Se muestra como dato de contacto en el pie." })}
                </div>
              </Card>

              <Card>
                <h2 className="micro-label mb-3">Aspecto y textos</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {input("primaryColorHex", "Color principal", { placeholder: "#1F3A5F", hint: "Formato #RRGGBB. Vacío: color por defecto." })}
                </div>
                <div className="mt-3 space-y-3">
                  <Field
                    label="Texto de notas"
                    htmlFor="tpl-notesText"
                    hint={!isGlobal && inherited?.notesText ? `Vacío: hereda «${inherited.notesText}»` : "Aparece bajo las líneas de la factura."}
                  >
                    <Textarea id="tpl-notesText" rows={3} value={form.notesText} onChange={(event) => change("notesText", event.target.value)} />
                  </Field>
                  <Field
                    label="Pie de página"
                    htmlFor="tpl-footerText"
                    hint={!isGlobal && inherited?.footerText ? `Vacío: hereda «${inherited.footerText}»` : "Por ejemplo, datos de inscripción registral."}
                  >
                    <Textarea id="tpl-footerText" rows={2} value={form.footerText} onChange={(event) => change("footerText", event.target.value)} />
                  </Field>
                </div>
              </Card>

              <Card>
                <h2 className="micro-label mb-3">Logo</h2>
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-32 flex-none items-center justify-center rounded-md border border-ink-200 bg-white">
                    {data.effectiveLogoDataUrl ? (
                      <img src={data.effectiveLogoDataUrl} alt="Logo que se usará en la factura" className="h-full w-full object-contain p-1" />
                    ) : (
                      <span className="text-xs text-ink-500">Sin logo</span>
                    )}
                  </div>
                  <p className="text-xs text-ink-500 dark:text-ink-400">
                    En la factura se usará {LOGO_SOURCE_LABEL[data.effectiveLogoSource]}.
                  </p>
                </div>
                <div className="mt-4 border-t border-ink-200 pt-4 dark:border-ink-800">
                  <Field label="Subir logo" htmlFor="tpl-logo" hint="PNG o JPG, hasta 700 KB.">
                    <input
                      id="tpl-logo"
                      type="file"
                      accept="image/png,image/jpeg"
                      className="field"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (file) void handleLogo(file);
                      }}
                    />
                  </Field>
                  {upload.isPending ? <p className="mt-2 text-sm text-ink-500">Subiendo…</p> : null}
                  {data.ownLogoDataUrl ? (
                    <div className="mt-3">
                      <Button variant="quiet" onClick={() => void handleRemoveLogo()} disabled={removeLogo.isPending}>
                        Quitar el logo propio
                      </Button>
                    </div>
                  ) : null}
                </div>
              </Card>

              <div className="flex flex-wrap gap-2">
                <Button variant="primary" onClick={() => void handleSave()} disabled={save.isPending || !dirty}>
                  {save.isPending ? "Guardando…" : "Guardar plantilla"}
                </Button>
                <Button onClick={() => void handlePreview()} disabled={preview.isPending}>
                  {preview.isPending ? "Generando…" : "Vista previa"}
                </Button>
                {!isGlobal && data.exists ? (
                  <Button variant="danger" onClick={() => void handleRemoveTenantTemplate()} disabled={remove.isPending}>
                    Quitar plantilla propia
                  </Button>
                ) : null}
              </div>
              {dirty ? <p className="text-xs text-ink-500 dark:text-ink-400">Hay cambios sin guardar. La vista previa usa lo que ves en el formulario.</p> : null}
            </div>

            <div>
              <h2 className="micro-label mb-2">Vista previa</h2>
              {previewUrl ? (
                <iframe title="Vista previa de la factura" src={previewUrl} className="h-[42rem] w-full rounded-lg border border-ink-200 bg-white" />
              ) : (
                <Notice tone="neutral">
                  Pulsa «Vista previa» para ver una factura de ejemplo con estos datos. No se guarda nada ni se crea ninguna factura.
                </Notice>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
